import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import SectionHeader from '../../components/views/SectionHeader';
import SectionFooter from '../../components/views/SectionFooter';
import ToggleField from '../../components/inputs/toggleField';
import InputField from '../../components/inputs/inputField';

class BookingPublish extends Component {
	constructor(props) {
		super(props);

		this.state = {
			setupProgress: this.calculateSetupProgress(),
			publishSettings: {
				isPublished: props.bookingPage.page.published || false,
				publicUrl: props.organization && (props.organization.hostname ? `https://${props.organization.hostname}/0/` : `https://${props.organization.identifier}.simple.events/0/`) + props.bookingPage.page.pathSlug,
				requireApproval: props.bookingPage.requireApproval || false
			},
			editing: false,
			publishing: false
		};
	}

	componentDidUpdate(prevProps) {
		if (prevProps.bookingPage !== this.props.bookingPage) {
			this.setState({
				setupProgress: this.calculateSetupProgress()
			});
		}
	}

	calculateSetupProgress = () => {
		const { bookingPage } = this.props;
		const requirements = [];

		// Basic Information check
		const basicWarnings = [];
		const hasBasicInfo = !!(bookingPage.name && bookingPage.description);
		
		if (!hasBasicInfo) {
			basicWarnings.push("Page name and description are required");
		}

		requirements.push({
			name: 'Basic Information',
			check: hasBasicInfo,
			description: 'Page name and description are required',
			warnings: basicWarnings
		});
		// 1. Calendars > 0 requirement with availability check
		const hasCalendars = !!(bookingPage.calendars && bookingPage.calendars.length > 0);
		let calendarsWithAvailability = false;
		const calendarWarnings = [];
		
		if (hasCalendars) {
			// Check if at least one active calendar has availability configured
			calendarsWithAvailability = bookingPage.calendars.some(calendar => {
				// Calendar must be active
				if (calendar.active === false) return false;
				
				// Calendar must have a schedule with at least one available day
				if (!calendar.schedule) return false;
				
				const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
				const hasAvailableDay = daysOfWeek.some(day => {
					const daySchedule = calendar.schedule[day];
					if (!daySchedule || !daySchedule.available) return false;
					
					// Check if the day has valid time ranges
					const timeRanges = daySchedule.availableTimeRanges;
					if (!timeRanges || timeRanges.length === 0) return false;
					
					// Check if at least one time range has both start and end times
					return timeRanges.some(range => range.startTime && range.endTime);
				});
				
				return hasAvailableDay;
			});
		}

		const calendarCheck = hasCalendars && calendarsWithAvailability;
		
		if (!hasCalendars) {
			calendarWarnings.push("At least one calendar must be created and active");
		} else if (!calendarsWithAvailability) {
			calendarWarnings.push("Active calendars must have availability configured with at least one available day and time range");
		}

		requirements.push({
			name: 'Active Calendars',
			check: calendarCheck,
			description: 'At least one active calendar with availability must be configured',
			warnings: calendarWarnings
		});

		// 2. Registrant > 0, if private
		const registrantWarnings = [];
		if (!bookingPage.page.openToPublic) {
			const hasRegistrants = !!(bookingPage.registrants && bookingPage.registrants.length > 0);
			
			if (!hasRegistrants) {
				registrantWarnings.push("Private booking pages must have at least one registrant");
			}

			requirements.push({
				name: 'Registrants (Private)',
				check: hasRegistrants,
				description: 'Private booking pages must have at least one registrant',
				warnings: registrantWarnings
			});
		}


		// 3. Email templates requirement - check if required and has content
		const emailWarnings = [];
		const emailRequired = bookingPage.emailRequired || false;
		if (emailRequired) {
			const hasValidEmailTemplate = !!(
				bookingPage.emails && 
				(
					(bookingPage.emails.confirmationEmail && 
						(bookingPage.emails.confirmationEmail.html || 
						 (bookingPage.emails.confirmationEmail.emailComponents && 
						  bookingPage.emails.confirmationEmail.emailComponents.length > 0)
						)
					) ||
					(bookingPage.emails.reminderEmail && 
						(bookingPage.emails.reminderEmail.html || 
						 (bookingPage.emails.reminderEmail.emailComponents && 
						  bookingPage.emails.reminderEmail.emailComponents.length > 0)
						)
					)
				)
			);
			
			if (!hasValidEmailTemplate) {
				emailWarnings.push("Email templates must have at least 1 component or HTML code when required");
			}

			requirements.push({
				name: 'Email Templates',
				check: hasValidEmailTemplate,
				description: 'Email templates must have at least 1 component or HTML code when required',
				warnings: emailWarnings
			});
		}

		// 4. Booking form requirements
		const formWarnings = [];
		let bookingFormCheck = false;
		if (bookingPage.form && bookingPage.form.pages && bookingPage.form.pages.length > 0) {
			if (bookingPage.page.openToPublic) {
				// For public pages: firstname and email are required
				let hasEmail = false;
				let hasFirstName = false;

				bookingPage.form.pages.forEach(page => {
					page.sections?.forEach(section => {
						section.fields?.forEach(field => {
							if (field.id === '_email' || field.id === 'email') {
								hasEmail = true;
							}
							if (field.id === '_firstName' || field.id === 'firstName') {
								hasFirstName = true;
							}
						});
					});
				});

				bookingFormCheck = hasEmail && hasFirstName;
				if (!hasEmail) {
					formWarnings.push("Public booking forms must include an Email field");
				}
				if (!hasFirstName) {
					formWarnings.push("Public booking forms must include a First Name field");
				}
			} else {
				// For private pages: Page section Fields should > 1
				let totalFields = 0;
				bookingPage.form.pages.forEach(page => {
					page.sections?.forEach(section => {
						totalFields += section.fields?.length || 0;
					});
				});

				bookingFormCheck = totalFields > 1;
				if (totalFields <= 1) {
					formWarnings.push("Booking form must have more than 1 field in page sections");
				}
			}
		} else {
			formWarnings.push("A booking form must be configured");
		}

		requirements.push({
			name: 'Booking Form',
			check: bookingFormCheck,
			description: bookingPage.page.openToPublic
				? 'Public booking forms must include First Name and Email fields'
				: 'Booking form must have more than 1 field in page sections',
			warnings: formWarnings
		});

		// 5. Booking Page - booking form exists and is correctly attached
		const pageWarnings = [];
		const pageFormAttached = !!(
			bookingPage.page && 
			bookingPage.page.partials && 
			bookingPage.page.partials.find(partial =>
				partial.file === 'v1/bookingForm' &&
				partial.parameters?.find(param => 
					param.key === 'linkedForm' && 
					param.value !== '' &&
					param.value === bookingPage.form?._id
				)
			)
		);

		if (!pageFormAttached) {
			pageWarnings.push("The booking form must be added to the page design and correctly attached");
		}

		requirements.push({
			name: 'Page Design',
			check: pageFormAttached,
			description: 'The booking form must be added to the page design and correctly attached',
			warnings: pageWarnings
		});


		const passedRequirements = requirements.filter(req => req.check);
		const canPublish = passedRequirements.length === requirements.length;

		return {
			requirements,
			passedRequirements: passedRequirements.length,
			totalRequirements: requirements.length,
			canPublish
		};
	};

	handleSettingChange = (field, value) => {
		this.setState({
			publishSettings: {
				...this.state.publishSettings,
				[field]: value
			},
			editing: true
		});
	};

	cancelEdit = () => {
		this.setState({
			publishSettings: {
				requireApproval: this.props.bookingPage.requireApproval || false
			},
			editing: false
		});
	};

	copyToClipboard = (text) => {
		navigator.clipboard.writeText(text);
		// Could add toast notification here
		alert('Copied to clipboard');
	};

	render() {
		const { setupProgress, publishSettings, editing } = this.state;
		const { bookingPage, sectionName, organization } = this.props;

		const baseUrl = organization?.hostname || `${organization?.identifier}.simple.events`;
		const bookingUrl = publishSettings.publicUrl || `https://${baseUrl}/book/${bookingPage.page.pathSlug}`;

		return (
			<div className="sectionContainer">
				<SectionHeader
					title={sectionName}
					description="Review setup requirements and publish your booking page"
					breadcrumbs={[
						{
							name: bookingPage.name || 'Booking Page'
						}
					]}
				/>
				<hr />

				<div className="sectionBody">
					<div className="sectionBlock contained-h">
						<div className="outlineCard mt-20">
							<p className="mb-20">
								Ready to launch this booking page?<br />
								Just toggle the switch below to publish.<br />
								A link will generate to then share with registrants.
							</p>
							<ToggleField
								label={<strong>Get started by publishing the page</strong>}
								icons={false}
								checked={this.props.bookingPage.page.published}
								disabled={!setupProgress.canPublish}
								onChange={this.props.bookingPage.page.published ? this.props.unpublishBookingPage : this.props.publishBookingPage}
							/>
							{!setupProgress.canPublish && (
								<div>
									<p className="mt-20 mb-20 modalErrorMessage small">
										It appears that the booking page is incomplete.<br />
										Please finish setting up the unchecked items to publish the booking page:
									</p>
								</div>
							)}

							{setupProgress.requirements.map((requirement, index) => (
								<div key={index}>
									<div className={`mt-10 flex aic gap5 ${requirement.check ? "blue" : "neu"}`}>
										{requirement.check ?
											<i className="las la-check-circle"></i> :
											<i className="las la-times-circle"></i>}
										<p className={`small`}>{requirement.name}</p>
									</div>
									{requirement.warnings && requirement.warnings.length > 0 && (
										<div className="mt-5 ml-20">
											{requirement.warnings.map((warning, warningIndex) => (
												<p key={warningIndex} className="small red mb-5">{warning}</p>
											))}
										</div>
									)}
								</div>
							))}
						{this.props.bookingPage.page.published && (
							<>
								<div>
									<div className={`flex aic gap10 mt-20`}>
										<InputField
											label="Booking page link"
											required={false}
											disabled={true}
											value={bookingUrl}
											readOnly
										/>
										<button
											className={`neu ${this.state.linkCopied ? 'disabled' : ''}`}
											onClick={() => {
												this.copyToClipboard(bookingUrl);
												this.setState({ linkCopied: true });
												setTimeout(() => {
													this.setState({ linkCopied: false });
												}, 2000);
											}}
										>
											{this.state.linkCopied ? 'Link Copied' : `Copy link`}
										</button>
									</div>
								</div>
							</>
						)}
						</div>
					</div>
				</div>

				<SectionFooter
					updatedAt={bookingPage.updatedAt}
					saveEdit={this.saveSettings}
					cancelEdit={this.cancelEdit}
					editing={editing}
				/>
			</div>
		);
	}
}

export default withRouter(BookingPublish);
